/**
 * Status derivation for the public Molecule.dev status page.
 *
 * Pure functions + types that turn a `/health` probe and a `/status/history`
 * payload (both served by the Molecule.dev API — see the `api.molecule.dev`
 * endpoints this app polls) into render-ready rows. No fabricated uptime, no
 * green-by-default: every figure is derived from what was actually observed.
 *
 * Mirrors the API's `HEALTH_COMPONENT_KEYS` and the shape returned by
 * `GET /api/status/history`.
 */

/** A subsystem's rolled-up status as shown on one status-page row. */
/**
 * A row's status. `unknown` is only ever an ADVISORY row the API did not
 * report (an older API): shown as "Not reported", never as a false green and
 * never as a problem; it does not turn the headline.
 */
export type ComponentStatus = 'operational' | 'degraded' | 'down' | 'unknown'

/**
 * The subsystems the public health map covers, in display order. The status
 * page renders one row per key so a Redis/queue, sandbox, realtime or DB
 * outage is discoverable on its own row — never folded into a single conflated
 * "API" row that reads green during a real dependency outage.
 */
/**
 * Rows that show on their own but never turn the page headline: an operator
 * matter with no user-visible effect (mirrors the API's advisory components).
 */
export const ADVISORY_STATUS_KEYS: ReadonlySet<string> = new Set(['backups'])

export const STATUS_COMPONENT_KEYS = [
  'api',
  'db',
  'tenantDb',
  'sandboxes',
  'realtime',
  'redis',
  'aiDefaults',
  'migrations',
  'fleet',
  'alerting',
  'deployments',
  'sandboxCreation',
  'backups',
] as const

/** A key of the coarse public health map. */
export type StatusComponentKey = (typeof STATUS_COMPONENT_KEYS)[number]

/**
 * The coarse, public-safe per-component status map the API reports on
 * `/health`. Each value is the subsystem's own status — `'operational'` or
 * `'degraded'` (the server cannot self-report a full `'down'`; that is surfaced
 * page-side when the live fetch fails). Partial because an older/edge response
 * may omit it.
 */
export type HealthComponents = Partial<Record<StatusComponentKey, 'operational' | 'degraded'>>

/**
 * The minimal shape of the public `/health` response
 * (`{ status, timestamp, components }`). The status page reads the rolled-up
 * `status`, the per-component `components` map, and the `timestamp`; the
 * detailed (admin-only) payload is never requested.
 */
export interface HealthResponse {
  status?: string
  timestamp?: string
  /** Coarse per-subsystem status map — one entry per {@link StatusComponentKey}. */
  components?: HealthComponents
}

/**
 * The outcome of a single health probe — either an HTTP response was received
 * (carrying `ok` + the parsed body) or the request failed entirely (network
 * error / timeout → the API is treated as unreachable).
 */
export type HealthProbe =
  { reached: true; ok: boolean; body: HealthResponse | null } | { reached: false }

/**
 * Derive the **API process** row from a probe outcome. This row reflects only
 * whether the API process is up and answering — NOT the health of its
 * dependencies. A dependency that is degraded (DB, Redis, sandboxes, realtime)
 * shows on its OWN row via {@link deriveComponentRows}; it must not paint the
 * API row red.
 *
 * @param probe - The health probe outcome.
 * @returns The derived API-process status.
 */
export function deriveApiStatus(probe: HealthProbe): ComponentStatus {
  if (!probe.reached) return 'down'
  const status = probe.body?.status
  if (status === 'shutting_down') return 'down'
  if (status === 'ok' || status === 'degraded') return 'operational'
  // 2xx with an unrecognized/empty body still means the process answered.
  if (probe.ok) return 'operational'
  // A non-2xx with no recognizable health body — unreachable behind a gateway.
  return 'down'
}

/**
 * Map a reported coarse component value onto a page {@link ComponentStatus}. Only a value the API
 * actually reported as operational reads green: a missing or unrecognized one is `'unknown'`
 * ("Not reported"), never a guess in the good direction (R142-L1).
 */
function mapComponentHealth(value: unknown): ComponentStatus {
  if (value === 'operational') return 'operational'
  if (value === 'degraded') return 'degraded'
  if (value === 'down') return 'down'
  return 'unknown'
}

/** A render-ready per-subsystem status row for the status page. */
export interface StatusComponentRow {
  /** Stable subsystem key (drives the i18n label + `data-mol-id`). */
  key: StatusComponentKey
  /** The subsystem's derived status. */
  status: ComponentStatus
}

/**
 * Derive one row per subsystem ({@link STATUS_COMPONENT_KEYS}) from a probe
 * outcome — the coarse public health map the status page renders. A
 * Redis/queue, sandbox, realtime or DB outage lands on its own row instead of
 * reading green.
 *
 * - unreachable / `shutting_down` → the `api` row is `'down'` and every row behind it is
 *   `'unknown'` ("Not reported"): we could not ask them, which is not the same as
 *   them being down, and a dozen red rows for one unanswered request overstates it
 * - reached: the `api` row is {@link deriveApiStatus}; each dependency row comes
 *   from the reported `components` map (missing → `'operational'`, best effort)
 *
 * @param probe - The health probe outcome.
 * @returns Rows in {@link STATUS_COMPONENT_KEYS} order.
 */
export function deriveComponentRows(probe: HealthProbe): StatusComponentRow[] {
  const apiStatus = deriveApiStatus(probe)
  // When the API itself is down/unreachable we could not probe what is behind it:
  // only the API row is down; the rest are "not reported", never a guess either way.
  if (apiStatus === 'down') {
    return STATUS_COMPONENT_KEYS.map((key) => ({
      key,
      status: (key === 'api' ? 'down' : 'unknown') as ComponentStatus,
    }))
  }
  const components = (probe.reached && probe.body?.components) || undefined
  return STATUS_COMPONENT_KEYS.map((key) => ({
    key,
    status: key === 'api' ? apiStatus : mapComponentHealth(components?.[key]),
  }))
}

/** A semantic tone for a {@link ComponentStatus}, for ClassMap text tokens + icons. */
export type StatusTone = 'success' | 'warning' | 'error' | 'neutral'

/**
 * Map a {@link ComponentStatus} onto its semantic tone. Drives both the
 * ClassMap text-color token (`textSuccess`/`textWarning`/`textError`) and the
 * status icon — so the status page never hardcodes a hex colour.
 *
 * @param status - The component status.
 * @returns `'success'` (operational), `'warning'` (degraded), `'error'` (down).
 */
export function statusTone(status: ComponentStatus): StatusTone {
  if (status === 'operational') return 'success'
  if (status === 'degraded') return 'warning'
  if (status === 'unknown') return 'neutral'
  return 'error'
}

/**
 * Roll a set of per-component statuses up to a single overall status. The
 * overall is the WORST of the components: any `'down'` → `'down'`, else any
 * `'degraded'` → `'degraded'`, else `'operational'`. An empty set is
 * `'operational'` (nothing known-broken).
 *
 * @param statuses - The per-component statuses.
 * @returns The overall status.
 */
export function overallStatus(statuses: ComponentStatus[]): ComponentStatus {
  if (statuses.some((s) => s === 'down')) return 'down'
  if (statuses.some((s) => s === 'degraded')) return 'degraded'
  return 'operational'
}

/**
 * The page headline: the overall status of every row EXCEPT the advisory ones
 * ({@link ADVISORY_STATUS_KEYS}) — a degraded backups row shows on its own
 * line and does not turn the headline by itself, a degraded database still
 * does. The API counts an advisory row when its state is user-visible (backups
 * that cannot be restored refuse starts): when the API's own overall says
 * degraded and only an advisory row is, the headline follows the API.
 *
 * @param rows - The component rows.
 * @param apiStatus - The API's own overall (`status` in the health body), when reached.
 * @returns The headline status.
 */
export function headlineStatus(
  rows: Array<{ key: string; status: ComponentStatus }>,
  apiStatus?: string | null,
): ComponentStatus {
  const own = overallStatus(
    rows.filter((r) => !ADVISORY_STATUS_KEYS.has(r.key)).map((r) => r.status),
  )
  if (own !== 'operational') return own
  // A core row the API did not report cannot back a claim that everything is operational.
  if (rows.some((r) => !ADVISORY_STATUS_KEYS.has(r.key) && r.status === 'unknown'))
    return 'degraded'
  const advisoryBad = rows.some(
    (r) => ADVISORY_STATUS_KEYS.has(r.key) && (r.status === 'degraded' || r.status === 'down'),
  )
  return apiStatus && apiStatus !== 'ok' && advisoryBad ? 'degraded' : own
}

/**
 * An interruption shorter than this is a blip, not an outage: a deploy restart, a Fly
 * hiccup. It is still reported (its row, its duration, its cause) but never in red.
 */
export const BRIEF_OUTAGE_MS = 15 * 60 * 1000

/**
 * Total down + degraded time a history day tolerates before its block turns amber
 * (owner, 2026-10-29: under ten minutes of degradation stays green — a short blip is
 * not a yellow day). Missing observation time never colors a block; the day's tooltip
 * and the page's missing-data line still state it.
 */
export const DAY_DEGRADED_TOLERANCE_MS = 10 * 60 * 1000

/**
 * Consecutive failed live probes (30 s apart) before the page says the API is unreachable.
 * One timed-out request is a dropped packet; two in a row is worth saying.
 */
export const OUTAGE_CONFIRM_FAILURES = 2

/**
 * The headline for an API that stopped answering: amber while it has been gone for under
 * {@link BRIEF_OUTAGE_MS}, red only once it has stayed gone.
 *
 * @param downForMs - How long the API has been unreachable, in ms.
 * @returns `'degraded'` for a short outage, `'down'` for a sustained one.
 */
export function unreachableHeadline(downForMs: number): ComponentStatus {
  return Number.isFinite(downForMs) && downForMs >= BRIEF_OUTAGE_MS ? 'down' : 'degraded'
}

/**
 * Whether the newest recorded sample is old enough that the history is no longer being kept: the
 * recorder samples every five minutes, so fifteen minutes of silence means it has stopped. The
 * figures above it then stay frozen at their last value (R142-M2).
 *
 * @param lastSampleIso - The history’s newest sample time.
 * @param now - The reference time in epoch ms.
 * @param maxAgeMs - The age past which the history counts as stale.
 * @returns True when stale or unparseable.
 */
export function historyIsStale(
  lastSampleIso: string,
  now: number = Date.now(),
  maxAgeMs: number = 15 * 60 * 1000,
): boolean {
  const t = Date.parse(lastSampleIso)
  return !Number.isFinite(t) || now - t > maxAgeMs
}

/** Display label for each component status (i18n `defaultValue` source). */
export const STATUS_LABEL: Record<ComponentStatus, string> = {
  operational: 'Operational',
  degraded: 'Degraded',
  down: 'Down',
  unknown: 'Not reported',
}

/**
 * Format a "last checked" timestamp for the status page. Falls back to an
 * em-dash placeholder for a missing / unparseable value.
 *
 * @param iso - An ISO timestamp (or null/undefined).
 * @returns A locale time string, or `'—'`.
 */
export function formatCheckedAt(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleTimeString()
}

// ---------------------------------------------------------------------------
// Status page — incident history. Mirrors the shape returned by
// GET /api/status/history. The API derives every figure from recorded health
// samples; the page only renders it.
// ---------------------------------------------------------------------------

/** A derived per-day cell status — a component status, or "no samples recorded". */
export type DayStatus = ComponentStatus | 'no-data'

/** A derived per-calendar-day (UTC) status cell in the rolling history bar. */
export interface StatusDay {
  /** `YYYY-MM-DD` (UTC). */
  date: string
  /** Worst status observed that day, or `'no-data'` when nothing was recorded. */
  status: DayStatus
  /** True when at least one sample was recorded that day. */
  monitored: boolean
  /** Total samples recorded that day. */
  checks: number
  /** Of `checks`, how many were `operational`. */
  operationalChecks: number
  /** Milliseconds of this day the API was down. Absent from an older API. */
  downMs?: number
  /** Milliseconds of this day the API was degraded. Absent from an older API. */
  degradedMs?: number
  /** Time with missing observations, excluded from uptime. */
  unobservedMs?: number
}

/**
 * WHO caused an incident, as the API derives it from evidence. Mirrors the API's `IncidentCause`.
 * `unknown` is the honest answer when nothing (neither our deploy's marker, nor the process's own
 * crash shape, nor Fly.io's machine event log or public status feed) proves a cause.
 */
export type IncidentCause = 'planned-deploy' | 'crash' | 'fly-platform' | 'unknown'

/** A derived incident window — a contiguous run of non-operational samples. */
export interface StatusIncidentWindow {
  /** Worst status observed during the window (`'degraded'` or `'down'`). */
  status: Exclude<ComponentStatus, 'operational'>
  /**
   * WHO caused the window, from evidence: our own planned deploy, our crash, Fly.io's platform,
   * or `unknown` when nothing proves a cause. Absent from an older API.
   */
  cause?: IncidentCause
  /** The API's short public sentence for {@link cause}. Absent from an older API. */
  causeText?: string
  /** ISO timestamp of the first non-operational sample. */
  startedAt: string
  /** ISO timestamp the window ended, or `null` when ongoing. */
  endedAt: string | null
  /** Window duration in milliseconds. */
  durationMs: number
  /** Number of non-operational samples in the run. */
  checks: number
  /**
   * Which subsystems were degraded during the window (public display order).
   * Absent for windows recorded before the API stored per-sample detail —
   * the page says "not recorded" rather than guessing.
   */
  components?: string[]
  /** Public-safe per-component reasons (probe-authored, first-seen order). */
  reasons?: string[]
  /** The operator's post-resolution note: what the fix was. Public content. */
  note?: string
}

/** Full rolling-history payload from `GET /api/status/history`. */
export interface StatusHistory {
  currentStatus: ComponentStatus | null
  since: string | null
  windowDays: number
  totalChecks: number
  operationalChecks: number
  /** Observed uptime % of the monitored time, or `null` when no samples recorded. */
  uptimePercent: number | null
  /**
   * How long monitoring has actually been running, in days (one decimal) — what the uptime
   * figure covers. Absent from an older API (the page then falls back to `windowDays`).
   */
  monitoredDays?: number | null
  /** When the newest sample was recorded. Absent from an older API. */
  lastSampleAt?: string | null
  /** Checks that were due but not recorded, including recording gaps. */
  missedChecks?: number
  /** Time excluded from uptime because monitoring supplied no observations. */
  unobservedMs?: number
  downMs?: number
  degradedMs?: number
  days: StatusDay[]
  incidents: StatusIncidentWindow[]
}

/**
 * Support API versions that labelled a missing recorder as downtime. Match
 * only that version's exact, public reason; lifecycle outages stay intact.
 * New APIs already supply `unobservedMs` and pass through unchanged.
 *
 * @param history - The API payload.
 * @param now - Reference time for the observed coverage.
 * @returns History with missing monitoring excluded from service downtime.
 */
export function normalizeStatusHistory(history: StatusHistory, now = Date.now()): StatusHistory {
  if (history.unobservedMs !== undefined) return history
  const missingReason =
    'No health checks were recorded for this period: the API was down, restarting or unreachable.'
  const gaps = history.incidents.filter(
    (incident) =>
      incident.status === 'down' &&
      incident.checks === 0 &&
      incident.reasons?.includes(missingReason),
  )
  if (gaps.length === 0) return history
  const unobservedMs = gaps.reduce((sum, gap) => sum + gap.durationMs, 0)
  const downMs =
    history.downMs === undefined ? undefined : Math.max(0, history.downMs - unobservedMs)
  const observedMs = history.since ? now - Date.parse(history.since) - unobservedMs : NaN
  let uptimePercent: number | null = null
  if (
    Number.isFinite(observedMs) &&
    observedMs > 0 &&
    downMs !== undefined &&
    history.degradedMs !== undefined
  ) {
    const lost = downMs + history.degradedMs
    uptimePercent = Math.min(
      lost > 0 ? 99.999 : 100,
      Math.max(0, Math.round(((observedMs - lost) / observedMs) * 100_000) / 1000),
    )
  }
  return {
    ...history,
    currentStatus: gaps.some((gap) => gap.endedAt === null) ? null : history.currentStatus,
    downMs,
    unobservedMs,
    uptimePercent,
    monitoredDays: Number.isFinite(observedMs)
      ? Math.round((Math.max(0, observedMs) / 86_400_000) * 10) / 10
      : null,
    incidents: history.incidents
      .filter((incident) => !gaps.includes(incident))
      .map((incident) => {
        const start = Date.parse(incident.startedAt)
        const end = start + incident.durationMs
        const missing = gaps.reduce((sum, gap) => {
          const gapStart = Date.parse(gap.startedAt)
          return (
            sum + Math.max(0, Math.min(end, gapStart + gap.durationMs) - Math.max(start, gapStart))
          )
        }, 0)
        if (missing === 0) return incident
        return {
          ...incident,
          durationMs: Math.max(0, incident.durationMs - missing),
          endedAt: incident.endedAt ?? gaps.find((gap) => gap.endedAt === null)?.startedAt ?? null,
        }
      }),
    days: history.days.map((day) => {
      const dayStart = Date.parse(`${day.date}T00:00:00Z`)
      const missing = gaps.reduce((sum, gap) => {
        const start = Date.parse(gap.startedAt)
        return (
          sum +
          Math.max(
            0,
            Math.min(dayStart + 86_400_000, start + gap.durationMs) - Math.max(dayStart, start),
          )
        )
      }, 0)
      if (missing === 0) return day
      const remainingDown = Math.max(0, (day.downMs ?? 0) - missing)
      return {
        ...day,
        downMs: remainingDown,
        unobservedMs: missing,
        status:
          remainingDown > 0
            ? 'down'
            : (day.degradedMs ?? 0) > 0
              ? 'degraded'
              : day.checks > 0
                ? 'operational'
                : 'no-data',
      }
    }),
  }
}

/**
 * The render tone for a history day cell. Monitored days map to their status
 * tone (so the cell is tinted via a ClassMap theme token, never a hardcoded
 * hex); unmonitored (`'no-data'`) days are `'muted'` so the bar is honest about
 * the absence of data rather than implying "operational" green.
 *
 * @param status - The day's derived status.
 * @returns The status {@link StatusTone}, or `'muted'` for an unmonitored day.
 */
export function dayCellTone(status: DayStatus): StatusTone | 'muted' {
  return status === 'no-data' ? 'muted' : statusTone(status)
}

/**
 * The render tone for one history day, sized to the time actually lost: red only when
 * the API was down for {@link BRIEF_OUTAGE_MS} or more that day, amber when down +
 * degraded totals {@link DAY_DEGRADED_TOLERANCE_MS} (10 minutes) or more, and green
 * below that — super short blips do not make a yellow day (owner, 2026-10-29).
 * Missing observation time is disclosed (tooltip, missing-data line) but never colors
 * the block. An older API that sends no durations falls back to {@link dayCellTone}.
 *
 * @param day - The history day.
 * @returns The day's {@link StatusTone}, or `'muted'` for an unmonitored day.
 */
export function dayTone(day: StatusDay): StatusTone | 'muted' {
  if (day.status === 'no-data') return 'muted'
  if (day.downMs === undefined && day.degradedMs === undefined) return dayCellTone(day.status)
  const down = day.downMs ?? 0
  const lost = down + (day.degradedMs ?? 0)
  if (down >= BRIEF_OUTAGE_MS) return 'error'
  if (lost >= DAY_DEGRADED_TOLERANCE_MS) return 'warning'
  return 'success'
}

/**
 * Format a duration (ms) compactly for an incident window: `"45s"`, `"12m"`,
 * `"3h 5m"`, `"2d 4h"`. Non-finite / negative inputs floor to `"0s"`.
 *
 * @param ms - The duration in milliseconds.
 * @returns A compact human duration.
 */
export function formatIncidentDuration(ms: number): string {
  const total = Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0
  if (total < 60) return `${total}s`
  const minutes = Math.floor(total / 60)
  // Seconds stay under an hour: a 92-second restart reads "1m 32s", not "1m".
  if (minutes < 60) return total % 60 ? `${minutes}m ${total % 60}s` : `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    const remMin = minutes % 60
    return remMin ? `${hours}h ${remMin}m` : `${hours}h`
  }
  const days = Math.floor(hours / 24)
  const remHours = hours % 24
  return remHours ? `${days}d ${remHours}h` : `${days}d`
}

/**
 * A duration a visitor can take in at a glance — never seconds, never a stopwatch reading:
 * "under a minute", "about 25 minutes", "about 3 hours". Minutes round to the nearest 5 once past 10.
 *
 * @param ms - The duration in milliseconds.
 * @returns A short, rounded phrase.
 */
export function formatRoughDuration(ms: number): string {
  const minutes = Number.isFinite(ms) && ms > 0 ? ms / 60_000 : 0
  if (minutes < 1) return 'under a minute'
  if (minutes < 10) {
    const n = Math.round(minutes)
    return n <= 1 ? 'about a minute' : `about ${n} minutes`
  }
  if (minutes < 90) return `about ${Math.round(minutes / 5) * 5} minutes`
  const hours = minutes / 60
  if (hours < 24) {
    const n = Math.round(hours)
    return `about ${n} hours`
  }
  const days = Math.round(hours / 24)
  return days <= 1 ? 'about a day' : `about ${days} days`
}

/**
 * Everything a visitor needs from the incident history, in one place: the total time the service
 * was affected and the distinct reasons, with no per-window detail.
 *
 * @param groups - The consolidated incidents.
 * @returns The summed duration, the distinct cause sentences, and whether one is still ongoing.
 */
export function summarizeIncidents(groups: ReadonlyArray<ConsolidatedIncident>): {
  totalMs: number
  causes: string[]
  ongoing: boolean
} {
  const causes: string[] = []
  for (const g of groups)
    for (const text of g.causeTexts) if (!causes.includes(text)) causes.push(text)
  return {
    totalMs: groups.reduce((sum, g) => sum + g.durationMs, 0),
    causes,
    ongoing: groups.some((g) => g.endedAt === null),
  }
}

/**
 * How long the uptime figure actually covers, for its label: "2.8 days", "1 day", "5 hours".
 * Uses the API's `monitoredDays` (the real span); an older API's `since` (first sample) otherwise.
 *
 * @param history - The status history payload.
 * @param now - The clock, for the `since` fallback.
 * @returns The covered span in words.
 */
export function formatMonitoredSpan(
  history: Pick<StatusHistory, 'monitoredDays' | 'windowDays'> & { since?: string | null },
  now: number = Date.now(),
): string {
  // An older API sends no monitoredDays: the span is first sample → now, capped at the window.
  const sinceMs = history.since ? Date.parse(history.since) : NaN
  const fromSince = Number.isFinite(sinceMs)
    ? Math.min(history.windowDays, Math.round(((now - sinceMs) / 86_400_000) * 10) / 10)
    : null
  const days = history.monitoredDays ?? fromSince ?? history.windowDays
  if (days < 1) {
    const hours = Math.max(1, Math.round(days * 24))
    return hours === 1 ? '1 hour' : `${hours} hours`
  }
  return days === 1 ? '1 day' : `${days} days`
}

/**
 * Format a `YYYY-MM-DD` day key for a status-bar tooltip / label. Falls back to
 * the raw key for an unparseable value.
 *
 * @param date - The `YYYY-MM-DD` (UTC) day key.
 * @returns A locale date string, or the raw key.
 */
export function formatDayLabel(date: string): string {
  const d = new Date(`${date}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return date
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/**
 * Format an uptime percentage for display, e.g. `99.95` → `"99.95%"`. A `null`
 * (no samples recorded) renders as the em-dash placeholder so the page never
 * shows a misleading 0% / 100% for an unmonitored window.
 *
 * @param percent - The uptime percentage, or null.
 * @returns A display string.
 */
export function formatUptimePercent(percent: number | null): string {
  if (percent == null || !Number.isFinite(percent)) return '—'
  // Three decimals, rounded DOWN, so a window with any downtime never displays as 100%
  // (99.9994% is "99.999%"). Trailing zeros are trimmed by Number's own formatting.
  const shown = Math.floor(percent * 1000) / 1000
  return `${shown}%`
}

// ---------------------------------------------------------------------------
// Incident consolidation. The API records one incident window per contiguous
// run of non-operational samples, so a component that flaps in and out all
// evening surfaces as a wall of near-identical "Degraded" rows. Presentation
// belongs to this page: windows of the same ongoing situation are merged into
// ONE entry that visibly accumulates detail (span, windows, reasons), and only
// windows separated by a real gap render separately.
// ---------------------------------------------------------------------------

/**
 * Consecutive incident windows whose non-operational runs end closer together
 * than this are ONE situation for display — a component blipping in and out
 * reads as a single incident that keeps getting updated, not a row per blip.
 * Windows separated by a larger gap are "truly separate" and stay separate.
 */
export const INCIDENT_MERGE_GAP_MS = 3 * 60 * 60 * 1000

/**
 * The largest gap over which two windows carrying the SAME operator note still
 * merge (one piece of work annotated as one incident across days).
 */
export const INCIDENT_SAME_NOTE_MERGE_GAP_MS = 7 * 24 * 60 * 60 * 1000

/** A display group: one or more incident windows belonging to one situation. */
export interface ConsolidatedIncident {
  /** Worst status across the merged windows (`'degraded'` or `'down'`). */
  status: Exclude<ComponentStatus, 'operational'>
  /** ISO timestamp of the first window's start. */
  startedAt: string
  /** ISO timestamp the last window ended, or `null` while any window is ongoing. */
  endedAt: string | null
  /** Total time actually non-operational — the SUM of window durations, not the span. */
  durationMs: number
  /** The individual windows in start order — the group's honest timeline. */
  windows: StatusIncidentWindow[]
  /** Affected subsystems across all windows, first-seen order. */
  components: string[]
  /** Unique public-safe reasons across all windows, first-seen order. */
  reasons: string[]
  /** Unique operator resolution notes across all windows, first-seen order. */
  notes: string[]
  /** Unique causes across all windows, first-seen order. Empty from an older API. */
  causes: IncidentCause[]
  /** Unique cause sentences across all windows, first-seen order. Empty from an older API. */
  causeTexts: string[]
  /** True when more than one window was merged into this entry. */
  merged: boolean
}

/**
 * Whether a display group is a blip: over, and under {@link BRIEF_OUTAGE_MS} of total impact.
 * The page lists these on one quiet line instead of a red row each.
 *
 * @param group - A consolidated incident.
 * @returns True for a finished interruption shorter than the brief-outage threshold.
 */
export function isBriefIncident(group: ConsolidatedIncident): boolean {
  return group.endedAt !== null && group.durationMs < BRIEF_OUTAGE_MS
}

/** Severity rank so a merged group reports its WORST window status. */
function incidentStatusRank(status: StatusIncidentWindow['status']): number {
  return status === 'down' ? 2 : 1
}

/** Append `value` to `list` unless an identical string is already present. */
function pushUnique(list: string[], value: string | undefined): void {
  if (value && !list.includes(value)) list.push(value)
}

/**
 * Merge incident windows into display groups. Two consecutive windows (by
 * start time) join the same group when the gap between the previous window's
 * end and the next window's start is at most {@link INCIDENT_MERGE_GAP_MS};
 * anything farther apart starts a new group. Nothing is dropped: each group
 * keeps every window, the union of affected components, and the unique
 * reasons/notes, so the page can show one entry that was "updated" rather
 * than a row per blip.
 *
 * @param incidents - Raw incident windows from the history payload (any order).
 * @returns Groups sorted newest-first (the page's display order); the windows
 * inside each group stay oldest-first so a merged entry reads like a timeline
 * of updates.
 */
export function consolidateIncidents(incidents: StatusIncidentWindow[]): ConsolidatedIncident[] {
  const sorted = [...incidents].sort(
    (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
  )
  const groups: ConsolidatedIncident[] = []
  let current: ConsolidatedIncident | null = null
  let prevEndMs = Number.NaN

  for (const win of sorted) {
    const startMs = new Date(win.startedAt).getTime()
    const endMs = win.endedAt ? new Date(win.endedAt).getTime() : startMs
    // A window the operator annotated with the SAME note as the open group is
    // one incident with it whatever the gap (within a week): one piece of
    // infrastructure work can restart the platform twenty times over two days,
    // and a row per restart hides the story the note tells.
    const sameNote =
      current !== null &&
      typeof win.note === 'string' &&
      win.note.length > 0 &&
      current.notes.length === 1 &&
      current.notes[0] === win.note &&
      !Number.isNaN(prevEndMs) &&
      startMs - prevEndMs <= INCIDENT_SAME_NOTE_MERGE_GAP_MS
    const continues =
      sameNote ||
      (current && !Number.isNaN(prevEndMs) && startMs - prevEndMs <= INCIDENT_MERGE_GAP_MS)
    if (!continues) {
      current = {
        status: win.status,
        startedAt: win.startedAt,
        endedAt: win.endedAt ?? null,
        durationMs: Math.max(0, win.durationMs),
        windows: [win],
        components: [],
        reasons: [],
        notes: [],
        causes: [],
        causeTexts: [],
        merged: false,
      }
      for (const component of win.components ?? []) pushUnique(current.components, component)
      for (const reason of win.reasons ?? []) pushUnique(current.reasons, reason)
      pushUnique(current.notes, win.note)
      if (win.cause && !current.causes.includes(win.cause)) current.causes.push(win.cause)
      pushUnique(current.causeTexts, win.causeText)
      groups.push(current)
    } else if (current) {
      if (incidentStatusRank(win.status) > incidentStatusRank(current.status)) {
        current.status = win.status
      }
      current.durationMs += Math.max(0, win.durationMs)
      // Ongoing wins over closed; otherwise the latest window's end.
      current.endedAt =
        win.endedAt === null
          ? null
          : new Date(win.endedAt).getTime() > new Date(current.endedAt ?? 0).getTime()
            ? win.endedAt
            : current.endedAt
      current.windows.push(win)
      for (const component of win.components ?? []) pushUnique(current.components, component)
      for (const reason of win.reasons ?? []) pushUnique(current.reasons, reason)
      pushUnique(current.notes, win.note)
      if (win.cause && !current.causes.includes(win.cause)) current.causes.push(win.cause)
      pushUnique(current.causeTexts, win.causeText)
    }
    prevEndMs = Number.isNaN(endMs) ? startMs : endMs
  }

  for (const group of groups) group.merged = group.windows.length > 1
  // Newest-first to match the page's display order; windows stay oldest-first.
  return groups.sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
}

/**
 * Format an incident's start–end pair for one display line. When both ends
 * fall on the same LOCAL day this is `"14:22 – 15:28"`; a span crossing days
 * keeps its date on each end (`"Oct 1, 14:22 – Oct 2, 02:32"`). A missing end
 * (ongoing) renders the start alone. Unparseable input falls back to `'—'`.
 *
 * @param startIso - ISO timestamp of the start (required).
 * @param endIso - ISO timestamp of the end, or null/undefined while ongoing.
 * @returns A display range, or `'—'` when the start is missing/unparseable.
 */
export function formatIncidentRange(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): string {
  if (!startIso) return '—'
  const start = new Date(startIso)
  if (Number.isNaN(start.getTime())) return '—'
  if (!endIso) return start.toLocaleTimeString()
  const end = new Date(endIso)
  if (Number.isNaN(end.getTime())) return start.toLocaleTimeString()
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate()
  if (sameDay) return `${start.toLocaleTimeString()} – ${end.toLocaleTimeString()}`
  const withDay = (d: Date): string =>
    `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString()}`
  return `${withDay(start)} – ${withDay(end)}`
}
